import React from 'react';
import * as d3 from 'd3';
import { useTranslation } from 'react-i18next';
import { Empty } from 'antd';

import { getItemAlteredByScenario } from 'utils/rules';
import { getScoreColor } from 'utils/ranking';

const MARGIN = 132;
const MAX_TREND = 100;

// Style « spokes + graduations », couleurs alignées sur le chart de la page
// Scénarios (cf. SynthesisGraph) :
// - scénario actif : #FCC356 (orange clair),
// - autres scénarios : #FCC35645 (même teinte, atténuée),
// - baseline : contour blanc + aire var(--ant-color-text-base) @0.1.
const ACTIVE_COLOR = '#FCC356';
const SCENARIO_GHOST_COLOR = '#FCC35645';
// Grille (axes + ticks) : même couleur que les axes du chart de la page
// Scénarios (var(--ant-color-text-base)). Pierre.
const GRID_COLOR = 'var(--ant-color-text-base)';
// Marge intérieure du scale radial : le min ne part PAS du centre exact mais
// d'un petit rayon, pour laisser de la marge au centre. Pierre #162.
const INNER_MARGIN = 28;
// Rogne un peu de marge haute : on remonte le graphe (le libellé du haut
// garde ~11px de slack) et on réduit d'autant la hauteur du SVG, le bas
// restant inchangé. Pierre : « trop de marge en haut ».
const TOP_TRIM = 30;
// Aires/baseline comme la page Scénarios : remplissage var(--ant-color-text-base)
// @0.1 pour les séries (baseline + autres scénarios), contour baseline blanc.
const SCENARIO_FILL = 'var(--ant-color-text-base)';
const SCENARIO_FILL_OPACITY = 0.1;
const BASELINE_FILL = SCENARIO_FILL;
const BASELINE_FILL_OPACITY = SCENARIO_FILL_OPACITY;
const BASELINE_STROKE = 'white';

// Transition reprise de l'onglet Scénarios : le navigateur interpole
// l'attribut `d` du path (et les positions des points) quand les valeurs
// changent, donnant l'animation de morphing du polygone.
const RADAR_TRANSITION: React.CSSProperties = {
	transitionDuration: '1.3s',
	transitionTimingFunction: 'ease-in-out',
	transitionProperty: 'all'
};

// Transition de la COULEUR des axes (spokes, ticks, pastille) quand le scénario
// change : la teinte gris/vert/rouge s'anime au lieu de sauter. Pierre.
const AXIS_COLOR_TRANSITION: React.CSSProperties = {
	transitionDuration: '1.3s',
	transitionTimingFunction: 'ease-in-out',
	transitionProperty: 'stroke, fill, fill-opacity, background-color, opacity'
};

// Parse robuste de la tendance (stockée en string, ex. '50'), cappée 0-100.
const parseTrend = (value: any): number => {
	const n = typeof value === 'number' ? value : parseInt(value, 10);
	if (Number.isNaN(n)) return 50;
	return Math.max(0, Math.min(MAX_TREND, n));
};

type StakesRadarChartProps = {
	// Enjeux bruts (non altérés). On applique le scénario nous-mêmes.
	stakes: any[];
	// Scénario actif (null => baseline).
	scenario?: any | null;
	// Tous les scénarios du projet (accepté pour compat ; non rendu dans le
	// style « spokes » qui ne superpose que la baseline grise + l'actif orange).
	scenarios?: any[];
	width: number;
	height?: number;
	// Sélectionne un enjeu (clic point ou libellé).
	select?: (item: any) => void;
	// Bascule vers un scénario (clic sur son périmètre). null = baseline.
	onSelectScenario?: (id: string | null) => void;
};

/*
  Radar / spider chart des enjeux — style « spokes + graduations ».
  - Un axe par enjeu : un rayon droit gradué de petits ticks (pas de cercles).
  - Métrique radiale = la tendance (trend, 0-100) de l'enjeu.
  - Deux séries superposées : la baseline en aire grise pleine (repère statique)
    et le scénario ACTIF en contour orange (points marqués) par-dessus.
  - Quand le scénario actif change, son contour se transforme avec une animation
    fluide (transition CSS sur l'attribut `d` du path et les positions des
    points) ; la baseline grise reste fixe comme repère.
*/
export const StakesRadarChart = ({
	stakes,
	scenario,
	scenarios,
	width,
	height,
	select,
	onSelectScenario
}: StakesRadarChartProps) => {
	const { t } = useTranslation();

	// Tooltip scénario suivant le curseur : on mémorise la position souris + le
	// contenu, et on rend un Tooltip antd ancré sur un point invisible placé à
	// cette position (placement fiable, près du curseur). Pierre.
	const [hoverTip, setHoverTip] = React.useState<
		{ x: number; y: number; label: string; description?: string } | null
	>(null);

	// Axes = enjeux activés dans la baseline. On garde un référentiel d'axes
	// stable (indépendant du scénario actif) pour que le morphing du polygone
	// reste cohérent d'un scénario à l'autre.
	const axes = React.useMemo(() => {
		return (stakes || [])
			.map((s) => ({ raw: s, base: getItemAlteredByScenario(s, null) }))
			.filter((entry) => entry.base && entry.base.enabled !== false)
			.map((entry, index) => ({
				id: entry.raw.id || `stake-${index}`,
				name: entry.base.name || entry.raw.name || '',
				raw: entry.raw
			}));
	}, [stakes]);

	// Indexation rapide enjeu -> objet brut pour calculer le trend par scénario.
	const rawById = React.useMemo(() => {
		const map: Record<string, any> = {};
		(stakes || []).forEach((s, index) => {
			map[s.id || `stake-${index}`] = s;
		});
		return map;
	}, [stakes]);

	const activeScenarioId = scenario?.id ?? null;

	// Valeur de trend d'un enjeu pour un scénario donné. Un enjeu désactivé
	// dans le scénario collapse vers le centre (0) -> rendu discret.
	const trendFor = React.useCallback(
		(axisId: string, sc: any | null): number => {
			const raw = rawById[axisId];
			if (!raw) return 0;
			const altered = getItemAlteredByScenario(raw, sc);
			if (!altered || altered.enabled === false) return 0;
			return parseTrend(altered.trend);
		},
		[rawById]
	);

	// Série ACTIVE : une seule série, dont les valeurs suivent le scénario
	// actif courant (null => baseline). C'est le SEUL path animé : sa clé React
	// est constante (cf. rendu plus bas), donc quand `scenario` change, le même
	// élément voit son `d`/ses points changer et la transition CSS le fait
	// morpher vers le nouveau périmètre.
	const activeSeries = React.useMemo(() => {
		const label =
			activeScenarioId === null
				? t('models.scenario.baseline')
				: scenario?.name || t('models.scenario.name');
		return {
			label,
			values: axes.map((a) => trendFor(a.id, scenario ?? null))
		};
	}, [axes, scenario, activeScenarioId, trendFor, t]);

	// Série de RÉFÉRENCE (baseline) : aire grise pleine, statique. Sert de
	// repère toujours visible sous le contour orange actif. Pas d'animation :
	// elle ne morphe pas, le scénario actif anime par-dessus.
	const baselineSeries = React.useMemo(() => {
		return {
			label: t('models.scenario.baseline'),
			values: axes.map((a) => trendFor(a.id, null))
		};
	}, [axes, trendFor, t]);

	// Périmètre de TOUS les scénarios, tracé en faible opacité comme repère de
	// contexte (on devine la dispersion des scénarios). Contour seul, STATIQUE
	// (pas d'animation), dessous le scénario actif. On inclut le scénario actif :
	// son contour statique marque la DESTINATION pendant que l'orange morphe vers
	// lui -> le scénario vers lequel on transitionne ne disparaît pas. À l'arrêt,
	// l'orange (plus épais/opaque) recouvre exactement ce contour. Pierre #184bis.
	const allScenariosSeries = React.useMemo(() => {
		return (scenarios || [])
			.filter((sc) => sc)
			.map((sc) => ({
				id: sc.id,
				label: sc.name || t('models.scenario.name'),
				description: sc.description || '',
				values: axes.map((a) => trendFor(a.id, sc))
			}));
	}, [scenarios, axes, trendFor, t]);

	const resolvedHeight = height ?? Math.max(360, Math.min(width, 760));
	const outerRadius = Math.min(width, resolvedHeight) / 2 - MARGIN;

	// Un radar a besoin d'au moins 3 axes pour avoir du sens.
	if (axes.length < 3) {
		return (
			<Empty
				description={t('pages.stakes.title')}
				style={{ padding: '4rem 0' }}
			/>
		);
	}

	// Échelle angulaire : un secteur par enjeu.
	const angleScale = d3
		.scaleBand<string>()
		.domain(axes.map((a) => a.id))
		.range([0, 2 * Math.PI]);

	// Échelle radiale (commune à tous les axes : même métrique 0-100).
	// Linéaire (et non scaleRadial/sqrt) : graduations régulières, échelle non
	// "exponentielle". Pierre #187.
	const radiusScale = d3
		.scaleLinear()
		.domain([0, MAX_TREND])
		.range([INNER_MARGIN, outerRadius]);

	const lineGenerator = d3.lineRadial();

	const angleFor = (id: string) => (angleScale(id) ?? 0) - Math.PI / 2;

	const pointFor = (id: string, value: number): [number, number] => {
		const angle = angleFor(id);
		const r = radiusScale(value) ?? 0;
		return [r * Math.cos(angle), r * Math.sin(angle)];
	};

	// Graduations le long de chaque axe : alignées sur les mêmes valeurs que les
	// sliders enjeux de la page (MiniSlider divise 0-100 en 6 segments égaux,
	// marques à 0 / 16.7 / 33.3 / 50 / 66.7 / 83.3 / 100). On rend les ticks
	// internes, du 2e au n-1 (on exclut 0 et 100). Placés via radiusScale -> ils
	// tombent sur les vraies valeurs. Pierre #184.
	const SLIDER_DIVISIONS = 6;
	const tickLevels = Array.from(
		{ length: SLIDER_DIVISIONS - 1 },
		(_, i) => ((i + 1) * MAX_TREND) / SLIDER_DIVISIONS
	);
	const TICK_HALF = 5; // demi-longueur des ticks (px), centrés sur l'axe.

	// Construit le path d'une série à partir de ses valeurs (1 par axe).
	const buildPath = (vals: number[]) => {
		const coords = axes.map((a, i) => {
			const r = radiusScale(vals[i] ?? 0) ?? 0;
			// lineRadial attend [angle, radius] avec 0 en haut.
			return [(angleScale(a.id) ?? 0), r] as [number, number];
		});
		if (coords[0]) coords.push(coords[0]);
		return lineGenerator(coords);
	};

	return (
		<>
		{/* Tooltip scénario suivant le curseur (div stylé comme antd, positionné
		    en fixed près de la souris -> placement fiable). */}
		{hoverTip && (
			<div
				style={{
					position: 'fixed',
					left: hoverTip.x + 12,
					top: hoverTip.y + 12,
					zIndex: 2000,
					pointerEvents: 'none',
					maxWidth: 360,
					background: 'rgba(0, 0, 0, 0.85)',
					color: '#fff',
					padding: '8px 10px',
					borderRadius: 6,
					boxShadow: '0 6px 16px 0 rgba(0,0,0,0.2)',
					fontSize: 12,
					lineHeight: 1.4
				}}
			>
				<div style={{ fontWeight: 600 }}>{hoverTip.label}</div>
				{hoverTip.description ? (
					<div style={{ fontSize: '0.85em', opacity: 0.85, marginTop: 6 }}>
						{hoverTip.description}
					</div>
				) : null}
			</div>
		)}
		<svg
			width={width}
			height={resolvedHeight - TOP_TRIM}
			role="img"
			focusable={false}
			tabIndex={-1}
			style={{ outline: 'none' }}
			aria-label={t('pages.risks.visualization_tab')}
			data-testid="stakes-radar-chart"
		>
			<g transform={`translate(${width / 2},${resolvedHeight / 2 - TOP_TRIM})`}>
				{/* Référence (baseline) : aire grise pleine, dessinée SOUS la grille
				    pour que les rayons/ticks restent nets PAR-DESSUS (sinon l'aire
				    les délave). Repère statique, ne morphe pas. Pierre #159/#160. */}
				{(() => {
					const d = buildPath(baselineSeries.values);
					if (!d) return null;
					return (
						<path
							key="baseline-series"
							d={d}
							fill={BASELINE_FILL}
							fillOpacity={BASELINE_FILL_OPACITY}
							stroke={BASELINE_STROKE}
							strokeWidth={1}
							strokeLinejoin="round"
							strokeLinecap="round"
						>
							<title>{baselineSeries.label}</title>
						</path>
					);
				})()}
				{/* Grille « spokes + graduations » : chaque axe est un rayon
				    droit, gradué de petits ticks perpendiculaires (style règle).
				    Pas de cercles/polygones concentriques. Libellé en bout d'axe. */}
				{axes.map((a, axisIndex) => {
					const [ox, oy] = pointFor(a.id, MAX_TREND);
					const angle = angleFor(a.id);
					// Axes (spokes + ticks) sans couleur : tout en gris-grille.
					// L'évolution vs baseline est portée uniquement par la pastille
					// de bout d'axe. Pierre.
					// Le rayon NE part PAS du centre exact : il démarre à la marge
					// intérieure (radiusScale(0) = INNER_MARGIN). Pierre #161/#162.
					const innerR = radiusScale(0) ?? 0;
					// Décalage du libellé au-delà du bout d'axe : doit dépasser la
					// plus grosse pastille (rayon max ~9.5 -> diamètre ~19) + une marge,
					// pour que le texte ne colle pas aux pastilles. Pierre.
					const labelRadius = outerRadius + 34;
					const lx = labelRadius * Math.cos(angle);
					const ly = labelRadius * Math.sin(angle);
					// Le <text> SVG ne sait pas faire de retour à la ligne :
					// on utilise un <foreignObject> + <div> HTML avec un
					// line-clamp 3 lignes + ellipsis. L'ancrage du bloc dépend
					// du quadrant de l'axe (cos -> horizontale, sin -> verticale),
					// pour que le libellé « pousse » vers l'extérieur du radar.
					const cos = Math.cos(angle);
					const sin = Math.sin(angle);
					// Seuil d'angle : en deçà (axe ~ vertical, haut/bas) on
					// centre ; au-delà on aligne à gauche/droite selon le signe.
					const COS_THRESHOLD = 0.25;
					const FO_WIDTH = 132; // ~3 lignes lisibles sans empiéter.
					const FO_LINE_HEIGHT = 14.4; // lineHeight 1.2 * font 12.
					const FO_HEIGHT = Math.ceil(FO_LINE_HEIGHT * 3) + 2; // ~45
					// Le SVG rogne ce qui dépasse son cadre. On borne la largeur du
					// libellé à l'espace réellement disponible jusqu'au bord (groupe
					// centré -> bords à ±width/2) : le texte s'ellipse proprement au
					// lieu d'être coupé en plein mot. Pierre #188.
					const halfW = width / 2;
					const EDGE_PAD = 6;
					let foX: number;
					let foWidth: number;
					let textAlign: React.CSSProperties['textAlign'];
					if (cos > COS_THRESHOLD) {
						// Axe à droite : div aligné à gauche, démarre au point.
						textAlign = 'left';
						foWidth = Math.max(40, Math.min(FO_WIDTH, halfW - lx - EDGE_PAD));
						foX = lx;
					} else if (cos < -COS_THRESHOLD) {
						// Axe à gauche : div aligné à droite, finit au point.
						textAlign = 'right';
						foWidth = Math.max(40, Math.min(FO_WIDTH, lx + halfW - EDGE_PAD));
						foX = lx - foWidth;
					} else {
						// Axe en haut/bas : centré horizontalement sur le point.
						textAlign = 'center';
						foWidth = FO_WIDTH;
						foX = lx - foWidth / 2;
					}
					// Verticalement : haut au-dessus du point, bas en-dessous,
					// latéraux centrés.
					let foY: number;
					let justify: React.CSSProperties['justifyContent'];
					if (sin < -COS_THRESHOLD) {
						foY = ly - FO_HEIGHT; // haut : bloc au-dessus du point
						justify = 'flex-end';
					} else if (sin > COS_THRESHOLD) {
						foY = ly; // bas : bloc en-dessous du point
						justify = 'flex-start';
					} else {
						foY = ly - FO_HEIGHT / 2; // latéral : centré
						justify = 'center';
					}
					// Pastille en BOUT d'axe : toujours pleine, couleur = couleur réelle
					// de l'enjeu sur la gauge (getScoreColor de la valeur du scénario
					// actif), comme dans la summary column / les gauges d'impact. Son
					// rayon dépend du poids de l'enjeu (1-3). Pierre.
					const actVal = activeSeries.values[axisIndex] ?? 0;
					const dotColor = getScoreColor(actVal);
					// Poids borné 1-3 ; rayon interpolé linéairement entre 2.5 (poids
					// min) et 8 (poids max) -> 2.5 / 5.25 / 8. Pierre.
					const weight = Math.max(1, Math.min(3, Math.round(Number(a.raw?.weight) || 1)));
					const DOT_R_MIN = 2.5;
					const DOT_R_MAX = 8;
					const dotRadius = DOT_R_MIN + ((weight - 1) / (3 - 1)) * (DOT_R_MAX - DOT_R_MIN);
					// Direction perpendiculaire à l'axe pour les ticks.
					const perpX = -Math.sin(angle);
					const perpY = Math.cos(angle);
					return (
						<g key={`axis-${a.id}`}>
							{/* Rayon (spoke) : démarre à la valeur min (innerR), pas au
							    centre -> pas de hub continu. */}
							<line
								x1={innerR * cos}
								y1={innerR * sin}
								x2={ox}
								y2={oy}
								stroke={GRID_COLOR}
								strokeWidth={0.5}
							/>
							{/* Pastille d'évolution en bout d'axe : toujours pleine, couleur
							    gauge (getScoreColor), rayon ~ poids, décalée de 5px du bout
							    de l'axe. */}
							{/* Marge de clic invisible autour de la pastille. */}
							<circle
								cx={ox + cos * (dotRadius + 5)}
								cy={oy + sin * (dotRadius + 5)}
								r={dotRadius + 8}
								fill="transparent"
								pointerEvents="all"
								onClick={() => select && select(a.raw)}
								style={{ cursor: select ? 'pointer' : 'default' }}
							/>
							<circle
								cx={ox + cos * (dotRadius + 5)}
								cy={oy + sin * (dotRadius + 5)}
								r={dotRadius}
								fill={dotColor}
								pointerEvents="none"
								style={AXIS_COLOR_TRANSITION}
							/>
							{/* Graduations perpendiculaires régulières */}
							{tickLevels.map((level) => {
								const r = radiusScale(level) ?? 0;
								const tx = r * Math.cos(angle);
								const ty = r * Math.sin(angle);
								return (
									<line
										key={`tick-${a.id}-${level}`}
										x1={tx - perpX * TICK_HALF}
										y1={ty - perpY * TICK_HALF}
										x2={tx + perpX * TICK_HALF}
										y2={ty + perpY * TICK_HALF}
										stroke={GRID_COLOR}
										strokeWidth={0.5}
									/>
								);
							})}
							{/* Libellé en bout d'axe : foreignObject + div HTML pour
							    le wrap 3 lignes + ellipsis (le <text> SVG ne wrappe pas). */}
							<foreignObject
								x={foX}
								y={foY}
								width={foWidth}
								height={FO_HEIGHT}
								style={{ overflow: 'visible' }}
							>
								<div
									title={a.name}
									onClick={() => select && select(a.raw)}
									style={{
										display: 'flex',
										flexDirection: 'column',
										justifyContent: justify,
										height: '100%',
										fontSize: 12,
										lineHeight: 1.2,
										color: 'var(--ant-color-text)',
										textAlign,
										cursor: select ? 'pointer' : 'default'
									}}
								>
									<span
										style={{
											display: '-webkit-box',
											WebkitLineClamp: 3,
											// eslint-disable-next-line @typescript-eslint/no-explicit-any
											lineClamp: 3 as any,
											WebkitBoxOrient: 'vertical',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											wordBreak: 'break-word'
										}}
									>
										{a.name}
									</span>
								</div>
							</foreignObject>
						</g>
					);
				})}

				{/* Périmètre de TOUS les scénarios en très faible opacité : repère
				    de contexte sous le scénario actif. Contour seul, pas d'aire,
				    pas d'animation. Pierre #184bis. */}
				{allScenariosSeries.map((s) => {
					const d = buildPath(s.values);
					if (!d) return null;
					return (
						<g key={`scenario-ghost-${s.id}`}>
							{/* Zone de survol invisible élargie (~3x) le long du trait
							    (pointerEvents=stroke -> hit-test même transparent) qui
							    déclenche le tooltip suivant le curseur. */}
							<path
								d={d}
								fill="none"
								stroke="transparent"
								strokeWidth={42}
								strokeLinejoin="round"
								strokeLinecap="round"
								pointerEvents="stroke"
								style={{ cursor: onSelectScenario ? 'pointer' : 'default' }}
								onMouseMove={(e) =>
									setHoverTip({ x: e.clientX, y: e.clientY, label: s.label, description: s.description })
								}
								onMouseLeave={() => setHoverTip(null)}
								onClick={() => onSelectScenario && onSelectScenario(s.id ?? null)}
							/>
							<path
								d={d}
								fill={SCENARIO_FILL}
								fillOpacity={SCENARIO_FILL_OPACITY}
								stroke={SCENARIO_GHOST_COLOR}
								strokeWidth={1}
								strokeLinejoin="round"
								strokeLinecap="round"
								pointerEvents="none"
							/>
						</g>
					);
				})}

				{/* Série ACTIVE : UN seul groupe à clé React CONSTANTE
				    ("active-series"). Quand le scénario actif change, ce MÊME
				    élément voit son `d` (et les cx/cy de ses points) changer et
				    la transition CSS `all 1.3s` le fait morpher vers le nouveau
				    périmètre. Ne JAMAIS inclure l'id du scénario dans cette clé,
				    sinon React remonte l'élément et le morphing disparaît. */}
				{(() => {
					const d = buildPath(activeSeries.values);
					if (!d) return null;

					return (
						<g key="active-series">
							{/* Remplissage très léger : le contour prime, on laisse
							    transparaître l'aire grise dessous. pointerEvents none pour
							    ne pas intercepter le survol des traits des scénarios dessous. */}
							<path
								d={d}
								fill={ACTIVE_COLOR}
								fillOpacity={0.06}
								stroke="none"
								strokeLinejoin="round"
								pointerEvents="none"
								style={RADAR_TRANSITION}
							/>
							{/* Contour orange (comme la page Scénarios). L'évolution par
							    enjeu est portée par les pastilles de bout d'axe. Pierre. */}
							<path
								d={d}
								fill="none"
								stroke={ACTIVE_COLOR}
								strokeWidth={2.5}
								strokeOpacity={1}
								strokeLinejoin="round"
								strokeLinecap="round"
								style={RADAR_TRANSITION}
								onMouseMove={(e) =>
									setHoverTip({ x: e.clientX, y: e.clientY, label: activeSeries.label, description: scenario?.description })
								}
								onMouseLeave={() => setHoverTip(null)}
							/>
							{/* Pas de points à chaque valeur (idem viz Scénarios). Pierre #163. */}
						</g>
					);
				})()}
			</g>
			</svg>
		</>
	);
};

export default StakesRadarChart;
